import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import api from "~/services/api";

export const EMPTY_GLOBAL_FILTERS = {
  filial_id: "",
  category: "",
  session_status: "",
  min_days: "",
  max_days: "",
};

const fieldClass = "w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-normal text-gray-800 focus:outline-none focus:ring-2 focus:ring-sky-500";
const selectClass = `${fieldClass} cursor-pointer disabled:cursor-not-allowed`;

export default function ModalFilters({ opened, onClose, onApplyFilters, value, selectedFilial }) {
  const [formData, setFormData] = useState(value);
  const [filials, setFilials] = useState([]);

  useEffect(() => {
    if (!opened) return;
    setFormData(value);
    if (Number(selectedFilial?.id) !== 1) {
      setFilials(selectedFilial?.id ? [selectedFilial] : []);
      return;
    }
    let mounted = true;
    api.get("/filials")
      .then(({ data }) => { if (mounted) setFilials(data?.rows ?? []); })
      .catch(() => { if (mounted) setFilials([]); });
    return () => { mounted = false; };
  }, [opened, selectedFilial, value]);

  if (!opened) return null;

  const update = (field, nextValue) => {
    setFormData((current) => ({ ...current, [field]: nextValue }));
  };

  const apply = () => {
    const hasMin = formData.min_days !== "";
    const hasMax = formData.max_days !== "";
    const min = Number(formData.min_days);
    const max = Number(formData.max_days);
    if ((hasMin && (!Number.isInteger(min) || Math.abs(min) > 3650)) ||
        (hasMax && (!Number.isInteger(max) || Math.abs(max) > 3650)) ||
        (hasMin && hasMax && min > max)) {
      toast.error("Enter a valid day range (minimum cannot exceed maximum).");
      return;
    }
    if (formData.session_status === "terminated" && (hasMin || hasMax)) {
      toast.error("Expiration days are available only for active sessions.");
      return;
    }
    if (formData.category === "Ex-student" && (hasMin || hasMax)) {
      toast.error("Ex-students do not have expiration days.");
      return;
    }
    onApplyFilters(formData);
    onClose();
  };

  const clear = () => setFormData(EMPTY_GLOBAL_FILTERS);
  const canSelectFilial = Number(selectedFilial?.id) === 1;

  return (
    <div className="fixed inset-0 z-50 flex justify-end items-center py-6" style={{ backgroundColor: "rgba(0,0,0,.75)" }} onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="bg-white w-full max-w-md h-full flex flex-col shadow-2xl overflow-hidden rounded-l-2xl">
        <div className="flex items-center justify-between px-6 py-6 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-normal text-gray-900 uppercase">Filters</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close filters" className="p-2 rounded-md cursor-pointer hover:bg-gray-100"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 flex flex-col gap-6">
          <label className="flex flex-col gap-2 text-xs font-normal text-gray-600 uppercase">
            Filial
            <select value={canSelectFilial ? formData.filial_id : String(selectedFilial?.id ?? "")} disabled={!canSelectFilial} onChange={(event) => update("filial_id", event.target.value)} className={selectClass}>
              {canSelectFilial && <option value="">All filials</option>}
              {filials.filter((filial) => canSelectFilial ? Number(filial.id) !== 1 : true).map((filial) => (
                <option key={filial.id} value={String(filial.id)}>{filial.name}</option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-2 text-xs font-normal text-gray-600 uppercase">
            Student category
            <select value={formData.category} onChange={(event) => update("category", event.target.value)} className={selectClass}>
              <option value="">All categories</option>
              <option value="Prospect">Prospect</option>
              <option value="Student">Student</option>
              <option value="Ex-student">Ex-student</option>
            </select>
          </label>

          <label className="flex flex-col gap-2 text-xs font-normal text-gray-600 uppercase">
            Session status
            <select value={formData.session_status} onChange={(event) => update("session_status", event.target.value)} className={selectClass}>
              <option value="">All sessions</option>
              <option value="active">Active</option>
              <option value="terminated">Financially canceled</option>
            </select>
          </label>

          <div className="flex flex-col gap-2">
            <span className="text-xs font-normal text-gray-600 uppercase">Days until session expires</span>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs text-gray-600">From
                <input type="number" step="1" value={formData.min_days} onChange={(event) => update("min_days", event.target.value)} className={fieldClass} placeholder="e.g. 0" />
              </label>
              <label className="text-xs text-gray-600">To
                <input type="number" step="1" value={formData.max_days} onChange={(event) => update("max_days", event.target.value)} className={fieldClass} placeholder="e.g. 30" />
              </label>
            </div>
            <p className="text-xs text-gray-500">0 means expires today. Use negative days to include expired sessions.</p>
            <div className="flex flex-wrap gap-2">
              {[
                { label: "Expired", min: "-3650", max: "-1" },
                { label: "Next 30 days", min: "0", max: "30" },
                { label: "31–60 days", min: "31", max: "60" },
                { label: "61–90 days", min: "61", max: "90" },
              ].map((preset) => (
                <button key={preset.label} type="button" onClick={() => setFormData((current) => ({ ...current, min_days: preset.min, max_days: preset.max, session_status: "active" }))} className="border rounded px-2 py-1 text-xs text-sky-700 cursor-pointer hover:bg-sky-50">{preset.label}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-6 border-t border-gray-200 bg-gray-50 flex flex-col gap-3">
          <button type="button" onClick={clear} className="w-full py-2 text-sm text-red-600 bg-white border border-red-100 rounded-lg cursor-pointer hover:bg-red-50">Clean All Filters</button>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg cursor-pointer">Cancel</button>
            <button type="button" onClick={apply} className="flex-1 px-4 py-2 text-sm text-white bg-sky-700 rounded-lg cursor-pointer hover:bg-sky-800">Apply Filters</button>
          </div>
        </div>
      </div>
    </div>
  );
}
